import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Clock, Building2 } from "lucide-react";

export default function PaymentResult({ cancelled = false }) {
  const navigate = useNavigate();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled) return;
    const params = new URLSearchParams(window.location.search);
    const gcId = params.get("gc_payment_id");
    const sid = params.get("session_id");
    if (!gcId && !sid) { setState("error"); return; }
    const statusUrl = gcId ? `/billing/gocardless/status/${gcId}` : `/billing/status/${sid}`;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(statusUrl);
        setInfo(r.data);
        if (r.data.active) { setState("success"); return; }
        if (r.data.status === "failed" || r.data.payment_status === "failed") { setState("failed"); return; }
      } catch (err) { console.error(err); }
      if (attempts.current >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [cancelled]);

  const Wrap = ({ icon: Icon, tone, title, text }) => (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="payment-result">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
        <div className={`h-14 w-14 rounded-full mx-auto mb-4 flex items-center justify-center ${tone}`}><Icon className="h-7 w-7" /></div>
        <h1 className="font-head text-2xl font-extrabold tracking-tight mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground mb-6">{text}</p>
        <Button onClick={() => navigate("/acquisti")} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md w-full" data-testid="back-to-settings">
          <Building2 className="h-4 w-4 mr-2" />Torna agli acquisti
        </Button>
      </div>
    </div>
  );

  if (state === "checking") return <Wrap icon={Loader2} tone="bg-secondary text-[#007BFF] [&>svg]:animate-spin" title="Verifica del pagamento…" text="Stiamo confermando l'attivazione del condominio." />;
  if (state === "success") return <Wrap icon={CheckCircle2} tone="bg-emerald-100 text-emerald-600" title="Condominio attivato" text="Il condominio è stato attivato correttamente." />;
  if (state === "pending") return <Wrap icon={Clock} tone="bg-amber-100 text-amber-600" title="Pagamento in elaborazione" text="Il pagamento è in fase di conferma. Il condominio verrà attivato a breve." />;
  if (state === "failed") return <Wrap icon={XCircle} tone="bg-red-100 text-red-600" title="Pagamento non riuscito" text="Non è stato possibile completare il pagamento. Riprova dalla pagina Acquisti." />;
  if (state === "cancelled") return <Wrap icon={XCircle} tone="bg-secondary text-muted-foreground" title="Pagamento annullato" text="Nessun addebito effettuato. Puoi riprovare quando vuoi." />;
  return <Wrap icon={XCircle} tone="bg-red-100 text-red-600" title="Sessione non valida" text="Link di pagamento non valido." />;
}
